import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import trackEvent from 'lib/posthog/trackEvent';

const AUTOPLAY_MS = 6000;

const HomeBannerCarouselItem = ({
  ad,
  gaEventCategory,
  width,
  height,
  priority,
  index,
}) => {
  const bannerRef = useRef(null);
  const viewFiredRef = useRef(false);

  useIntersectionObserver({
    target: bannerRef,
    threshold: 0.5,
    // Fires once per banner per mount — ref guards against duplicate view events
    // when the user scrolls back to a previously seen banner.
    onIntersect: () => {
      if (viewFiredRef.current) return;
      viewFiredRef.current = true;
      trackEvent(
        'view home banner',
        {
          type: 'banner',
          source_type: 'scroll',
          source_value: ad.description,
          event_category: 'home',
          event_label: ad.description,
          position: index,
        },
        { destination: 'ga4' }
      );
    },
  });

  return (
    <a
      ref={bannerRef}
      className="w-full flex-shrink-0 snap-start"
      href={ad.url}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => {
        sendEvent({
          action: `small_ad_click__${ad.id}`,
          category: gaEventCategory,
          label: ad.visibility,
        });
        trackEvent(
          'click home banner',
          {
            source_type: 'interaction',
            source_value: ad.description,
            event_category: 'home',
            event_label: ad.description,
            position: index,
          },
          { destination: 'ga4' }
        );
      }}
    >
      <picture className="w-full">
        {ad.mobileImage && (
          <source media="(max-width: 767px)" srcSet={ad.mobileImage} />
        )}
        {ad.tabletImage && (
          <source media="(max-width: 1023px)" srcSet={ad.tabletImage} />
        )}
        <img
          alt={ad.description}
          className="w-full rounded-3"
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          src={ad.image}
          width={width}
        />
      </picture>
    </a>
  );
};

const HomeBannerCarousel = ({ ads = [], gaEventCategory, width, height }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  const adsSorted = useMemo(
    () => [...ads].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [ads]
  );

  const scrollToIndex = useCallback((index) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  }, []);

  const advance = useCallback(() => {
    setActiveIndex((prev) => {
      const next = (prev + 1) % adsSorted.length;
      scrollToIndex(next);
      return next;
    });
  }, [adsSorted.length, scrollToIndex]);

  useEffect(() => {
    if (adsSorted.length <= 1) return;
    timerRef.current = setInterval(advance, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [advance, adsSorted.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Derives activeIndex from scroll position so the dot indicator stays in
    // sync when the user swipes manually (not via the autoplay advance()).
    const onScroll = () => {
      const index = Math.round(el.scrollLeft / el.clientWidth);
      setActiveIndex(Math.min(index, adsSorted.length - 1));
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [adsSorted.length]);

  if (adsSorted.length === 0) return null;

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className="flex w-full snap-x snap-mandatory overflow-x-scroll scroll-smooth scrollbar-none"
      >
        {adsSorted.map((ad, index) => (
          <HomeBannerCarouselItem
            key={ad.id}
            ad={ad}
            gaEventCategory={gaEventCategory}
            height={height}
            index={index}
            priority={index === 0}
            width={width}
          />
        ))}
      </div>

      {adsSorted.length > 1 && (
        <div className="mt-2 flex w-full items-center justify-center gap-x-2">
          {adsSorted.map((_, i) => (
            <div
              key={i}
              className={twJoin(
                'size-2 rounded-full transition-colors',
                i === activeIndex
                  ? 'bg-neutral-low-400 dark:bg-neutral-high-200'
                  : 'bg-neutral-high-300 dark:bg-neutral-low-300'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

HomeBannerCarousel.propTypes = {
  ads: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      mobileImage: PropTypes.string,
      tabletImage: PropTypes.string,
      url: PropTypes.string.isRequired,
      visibility: PropTypes.string,
      position: PropTypes.number,
    })
  ),
  gaEventCategory: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
};

export default HomeBannerCarousel;
